import { toDash } from '../utils/string';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupFragmentPlugin from '@swup/fragment-plugin';
import SwupParallelPlugin from '@swup/parallel-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import Swup from 'swup';
import { Scroll } from './Scroll';
import { CSS_CLASS, CUSTOM_EVENT } from '../config';
import { $html, $body } from '../utils/dom';

export class Load {
    constructor() {
        this.onVisitStartBind = this.onVisitStart.bind(this);
        this.beforeContentReplaceBind = this.beforeContentReplace.bind(this);
        this.onContentReplaceBind = this.onContentReplace.bind(this);
        this.onAnimationInEndBind = this.onAnimationInEnd.bind(this);
        this.onAnimationOutStartBind = this.onAnimationOutStart.bind(this);
        this.goTo = this.goTo.bind(this);
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        this.initSwup();
        this.bindEvents();

        requestAnimationFrame(() => {
            $html.classList.add(CSS_CLASS.READY);
        });
    }

    destroy() {
        this.unbindEvents();

        this.swup?.destroy();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        window.addEventListener(CUSTOM_EVENT.GO_TO, this.goTo);

        this.swup.hooks.on('visit:start', this.onVisitStartBind);
        this.swup.hooks.before('content:replace', this.beforeContentReplaceBind);
        this.swup.hooks.on('content:replace', this.onContentReplaceBind);
        this.swup.hooks.on('animation:in:end', this.onAnimationInEndBind);
        this.swup.hooks.on('animation:out:start', this.onAnimationOutStartBind);

        this.swup.hooks.on('fetch:error', (e) => {
            console.log('fetch:error:', e);
            debugger;
        });
        this.swup.hooks.on('fetch:timeout', (e) => {
            console.log('fetch:timeout:', e);
            debugger;
        });
    }

    unbindEvents() {
        window.removeEventListener(CUSTOM_EVENT.GO_TO, this.goTo);
    }

    // =============================================================================
    // Methods
    // =============================================================================
    initSwup() {
        this.swup = new Swup({
            animateHistoryBrowsing: true,
            plugins: this._getSwupPlugins()
        });
    }

    /**
     * Retrieve HTML dataset on next container and update our real html element dataset accordingly
     *
     * @param visit
     */
    updateDocumentAttributes(visit) {
        const parser = new DOMParser();
        const nextDOM = parser.parseFromString(visit.to.html, 'text/html');
        const newDataset = {
            ...nextDOM.querySelector('html')?.dataset
        };

        Object.entries(newDataset).forEach(([key, val]) => {
            $html.setAttribute(`data-${toDash(key)}`, val ?? '');
        });
    }

    _getSwupPlugins() {
        const rules = [
            {
                from: `/events(.*)`,
                to: `/events(.*)`,
                containers: ['#listing'],
                name: 'listing-update'
            },
            {
                from: `/blog(.*)`,
                to: `/blog(.*)`,
                containers: ['#listing'],
                name: 'listing-update'
            },
            {
                from: `/drake-art(.*)`,
                to: `/drake-art(.*)`,
                containers: ['#listing'],
                name: 'listing-update'
            }
        ];

        const plugins = [
            new SwupParallelPlugin(),
            new SwupPreloadPlugin({
                preloadHoveredLinks: true
            }),
            new SwupFragmentPlugin({
                rules: rules,
                debug: true
            }),
            new SwupA11yPlugin({
                announcements: {
                    'en-US': {
                        visit: 'Navigated to: {title}',
                        url: 'New page at {url}'
                    },
                    'de-DE': {
                        visit: 'Navigiert zu: {title}',
                        url: 'Neue Seite unter {url}'
                    },
                    'fr-FR': {
                        visit: 'Navigué vers : {title}',
                        url: 'Nouvelle page à {url}'
                    },
                    '*': {
                        visit: '{title}',
                        url: '{url}'
                    }
                }
            })
        ];

        return plugins;
    }

    // =============================================================================
    // Hooks
    // =============================================================================

    /**
     * On `transition.goTo` custom event
     * Navigate to a new page
     *
     * @param e
     */
    goTo(e) {
        this.swup.navigate(e.detail);
    }

    /**
     * On visit:start
     * Transition to a new page begins
     *
     * @see https://swup.js.org/hooks/#visit-start
     * @param visit
     */
    onVisitStart(visit) {
        if (!visit.fragmentVisit) {
            visit.scroll.reset = false;
            $html.classList.add(CSS_CLASS.TRANSITION);
            $html.classList.remove(CSS_CLASS.READY);
        }

        if (visit.fragmentVisit) {
            if (visit.fragmentVisit.name == 'listing-update') {
                Scroll.scrollTo('#listing', { offset: -200 });
            }
        }

        // Dispatch custom event
        window.dispatchEvent(
            new CustomEvent(CUSTOM_EVENT.VISIT_START, {
                detail: visit
            })
        );
    }

    /**
     * On before:content:replace
     * The old content of the page is replaced by the new content.
     *
     * @see https://swup.js.org/hooks/#content-replace
     * @param visit
     */
    beforeContentReplace(visit) {
        if (!visit.fragmentVisit) {
            Scroll?.destroy();
        }
    }

    /**
     * On content:replace
     * The old content of the page is replaced by the new content.
     *
     * @see https://swup.js.org/hooks/#content-replace
     * @param visit
     */
    onContentReplace(visit) {
        const adminBar = document.getElementById('wpadminbar');
        if (adminBar) {
            adminBar.remove();
            var parser = new DOMParser();
            var doc = parser.parseFromString(visit.to.html, 'text/html');
            var newAdminBar = doc.getElementById('wpadminbar');
            $body.appendChild(newAdminBar);
        }

        if (!visit.fragmentVisit) {
            Scroll?.init();
            this.updateDocumentAttributes(visit);
        }
    }

    /**
     * On animation:out:start
     * Current content starts animating out. Class `.is-animating` is added.
     *
     * @see https://swup.js.org/hooks/#animation-out-start
     * @param visit
     */
    onAnimationOutStart(visit) {
        if (!visit.fragmentVisit) {
            const scrollY = window.scrollY;
            $html.style.setProperty('--scroll-y', `${scrollY}px`);

            const transformOrigin = scrollY + window.innerHeight / 2;
            $html.style.setProperty('--transform-origin', `50% ${transformOrigin}px`);
        }
    }

    /**
     * On animation:in:end
     * New content finishes animating out.
     *
     * @see https://swup.js.org/hooks/#animation-in-end
     * @param visit
     */
    onAnimationInEnd(visit) {
        if (!visit.fragmentVisit) {
            window.scrollTo(0, 0);
            $html.classList.remove(CSS_CLASS.TRANSITION);
            $html.classList.add(CSS_CLASS.READY);
        }
    }
}
